import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Switch, Tabs, Tag, Space, Alert, Typography, Divider, Collapse, Modal } from 'antd';
import { FacebookOutlined, InstagramOutlined, ApiOutlined, CheckCircleOutlined, CloseCircleOutlined, SaveOutlined, LinkOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import { useTranslation } from 'react-i18next';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const SocialIntegrations = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const location = useLocation();
    const [facebookForm] = Form.useForm();
    const [instagramForm] = Form.useForm();
    const [activeTab, setActiveTab] = useState('facebook');

    // Check for OAuth success/error in URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const success = params.get('success');
        const error = params.get('error');

        if (success) {
            message.success(`${success.charAt(0).toUpperCase() + success.slice(1)} connected successfully!`);
            queryClient.invalidateQueries('social-integrations');
            // Clear URL params
            window.history.replaceState({}, '', location.pathname);
        }

        if (error) {
            let errorMessage = 'Failed to connect';
            switch (error) {
                case 'missing_params':
                    errorMessage = 'Missing authentication parameters';
                    break;
                case 'no_pages':
                    errorMessage = 'No Facebook pages found. Please create a page first.';
                    break;
                case 'no_instagram':
                    errorMessage = 'No Instagram account linked to your Facebook page';
                    break;
                case 'no_whatsapp':
                    errorMessage = 'No WhatsApp Business account found';
                    break;
                case 'no_phone':
                    errorMessage = 'No phone number configured for WhatsApp Business';
                    break;
                case 'oauth_failed':
                    errorMessage = 'Authentication failed. Please try again.';
                    break;
            }
            message.error(errorMessage);
            // Clear URL params
            window.history.replaceState({}, '', location.pathname);
        }
    }, [location, queryClient]);

    // Fetch integrations
    const { data: integrationsData, isLoading } = useQuery(
        'social-integrations',
        async () => {
            const res = await api.get('/social-integrations');
            return res.data;
        },
        {
            refetchOnMount: 'always'
        }
    );

    // Save integration mutation
    const saveIntegrationMutation = useMutation(
        async ({ platform, credentials, settings }) => {
            const res = await api.post('/social-integrations', {
                platform,
                credentials,
                settings
            });
            return res.data;
        },
        {
            onSuccess: (data) => {
                message.success(t('Integration saved successfully'));
                queryClient.invalidateQueries('social-integrations');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('Failed to save integration'));
            }
        }
    );

    // Toggle integration mutation
    const toggleIntegrationMutation = useMutation(
        async (platform) => {
            const res = await api.put(`/social-integrations/${platform}/toggle`);
            return res.data;
        },
        {
            onSuccess: () => {
                message.success(t('Integration status updated'));
                queryClient.invalidateQueries('social-integrations');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('Failed to update status'));
            }
        }
    );

    // Delete integration mutation
    const deleteIntegrationMutation = useMutation(
        async (platform) => {
            const res = await api.delete(`/social-integrations/${platform}`);
            return res.data;
        },
        {
            onSuccess: () => {
                message.success(t('Integration deleted successfully'));
                queryClient.invalidateQueries('social-integrations');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('Failed to delete integration'));
            }
        }
    );

    const getIntegration = (platform) => {
        return integrationsData?.data?.find(i => i.platform === platform);
    };

    const handleSaveFacebook = async (values) => {
        await saveIntegrationMutation.mutateAsync({
            platform: 'facebook',
            credentials: {
                pageId: values.pageId,
                pageAccessToken: values.pageAccessToken,
                appId: values.appId
            },
            settings: {
                autoReply: values.autoReply || false,
                notifications: values.notifications !== false
            }
        });
    };

    const handleSaveInstagram = async (values) => {
        await saveIntegrationMutation.mutateAsync({
            platform: 'instagram',
            credentials: {
                accountId: values.accountId,
                accessToken: values.accessToken
            },
            settings: {
                autoReply: values.autoReply || false,
                notifications: values.notifications !== false
            }
        });
    };

    const handleToggle = async (platform) => {
        await toggleIntegrationMutation.mutateAsync(platform);
    };

    const handleDelete = async (platform) => {
        Modal.confirm({
            title: t('Remove Connection'),
            content: t('Are you sure you want to remove this integration? You will need to reconnect to use it again.'),
            okText: t('Yes, Remove'),
            okType: 'danger',
            cancelText: t('cancel'),
            onOk: async () => {
                await deleteIntegrationMutation.mutateAsync(platform);
            }
        });
    };

    const handleConnectPlatform = async (platform) => {
        try {
            const res = await api.get(`/social-integrations/oauth/${platform}/url`);
            const oauthUrl = res.data.data.url;

            // Open OAuth URL in current window
            window.location.href = oauthUrl;
        } catch (error) {
            message.error(`Failed to connect ${platform}: ${error.response?.data?.message || error.message}`);
        }
    };

    const renderConnectionStatus = (platform) => {
        const integration = getIntegration(platform);

        if (!integration) {
            return <Tag icon={<CloseCircleOutlined />} color="default">{t('Not Connected')}</Tag>;
        }

        return integration.isActive ? (
            <Tag icon={<CheckCircleOutlined />} color="success">{t('Active')}</Tag>
        ) : (
                <Tag icon={<CloseCircleOutlined />} color="warning">{t('Inactive')}</Tag>
        );
    };

    return (
        <div>
            <Title level={2}>
                <ApiOutlined /> {t('Social Media Integrations')}
            </Title>

            <Alert
                message={t('Connect Your Social Platforms')}
                description={t('Receive and reply to messages from Facebook and Instagram directly in your academy dashboard. Convert leads to students with one click.')}
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                {/* Facebook Tab */}
                <TabPane
                    tab={
                        <span>
                            <FacebookOutlined /> {t('Facebook Messenger')}
                        </span>
                    }
                    key="facebook"
                >
                    <Card>
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <div>
                                {renderConnectionStatus('facebook')}
                                {getIntegration('facebook') && (
                                    <Space>
                                        <Button
                                            size="small"
                                            onClick={() => handleToggle('facebook')}
                                            loading={toggleIntegrationMutation.isLoading}
                                        >
                                            {getIntegration('facebook')?.isActive ? t('Deactivate') : t('Activate')}
                                        </Button>
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDelete('facebook')}
                                            loading={deleteIntegrationMutation.isLoading}
                                        >
                                            {t('delete')}
                                        </Button>
                                    </Space>
                                )}
                            </div>

                            <Alert
                                message={t('Easy Setup - One Click Connection')}
                                description={t("Click the button below to connect your Facebook page. You'll be redirected to Facebook to select a page and authorize access.")}
                                type="success"
                                showIcon
                            />

                            <Button
                                type="primary"
                                size="large"
                                icon={<LinkOutlined />}
                                onClick={() => handleConnectPlatform('facebook')}
                                style={{ width: '100%' }}
                            >
                                {getIntegration('facebook') ? t('Reconnect Facebook') : t('Connect Facebook Messenger')}
                            </Button>

                            <Collapse>
                                <Panel header={t('Manual Configuration (Advanced)')} key="1">
                                    <Form
                                        form={facebookForm}
                                        layout="vertical"
                                        onFinish={handleSaveFacebook}
                                        initialValues={getIntegration('facebook')?.facebook}
                                    >
                                        <Form.Item
                                            name="pageId"
                                            label={t('Page ID')}
                                            rules={[{ required: true, message: t('Please enter Page ID') }]}
                                        >
                                            <Input placeholder="123456789012345" />
                                        </Form.Item>

                                        <Form.Item
                                            name="pageAccessToken"
                                            label={t('Page Access Token')}
                                            rules={[{ required: true, message: t('Please enter Page Access Token') }]}
                                        >
                                            <Input.Password placeholder="EAAG..." />
                                        </Form.Item>

                                        <Form.Item
                                            name="appId"
                                            label={t('App ID')}
                                            rules={[{ required: true, message: t('Please enter App ID') }]}
                                        >
                                            <Input placeholder="123456789012345" />
                                        </Form.Item>

                                        <Form.Item
                                            name="autoReply"
                                            label={t('Enable Auto-Reply')}
                                            valuePropName="checked"
                                        >
                                            <Switch />
                                        </Form.Item>

                                        <Form.Item
                                            name="notifications"
                                            label={t('Enable Notifications')}
                                            valuePropName="checked"
                                            initialValue={true}
                                        >
                                            <Switch defaultChecked />
                                        </Form.Item>

                                        <Form.Item>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                icon={<SaveOutlined />}
                                                loading={saveIntegrationMutation.isLoading}
                                            >
                                                {t('Save Configuration')}
                                            </Button>
                                        </Form.Item>
                                    </Form>
                                </Panel>
                            </Collapse>
                        </Space>
                    </Card>
                </TabPane>

                {/* Instagram Tab */}
                <TabPane
                    tab={
                        <span>
                            <InstagramOutlined /> {t('Instagram Direct')}
                        </span>
                    }
                    key="instagram"
                >
                    <Card>
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <div>
                                {renderConnectionStatus('instagram')}
                                {getIntegration('instagram') && (
                                    <Space>
                                        <Button
                                            size="small"
                                            onClick={() => handleToggle('instagram')}
                                            loading={toggleIntegrationMutation.isLoading}
                                        >
                                            {getIntegration('instagram')?.isActive ? t('Deactivate') : t('Activate')}
                                        </Button>
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDelete('instagram')}
                                            loading={deleteIntegrationMutation.isLoading}
                                        >
                                            {t('delete')}
                                        </Button>
                                    </Space>
                                )}
                            </div>

                            <Alert
                                message={t('Easy Setup - One Click Connection')}
                                description={t("Click the button below to connect your Instagram Business account. You'll be redirected to Facebook to authorize access.")}
                                type="success"
                                showIcon
                            />

                            <Button
                                type="primary"
                                size="large"
                                icon={<LinkOutlined />}
                                onClick={() => handleConnectPlatform('instagram')}
                                style={{ width: '100%' }}
                            >
                                {getIntegration('instagram') ? t('Reconnect Instagram') : t('Connect Instagram Direct')}
                            </Button>

                            <Collapse>
                                <Panel header={t('Manual Configuration (Advanced)')} key="1">
                                    <Form
                                        form={instagramForm}
                                        layout="vertical"
                                        onFinish={handleSaveInstagram}
                                        initialValues={getIntegration('instagram')?.instagram}
                                    >
                                        <Form.Item
                                            name="accountId"
                                            label={t('Instagram Account ID')}
                                            rules={[{ required: true, message: t('Please enter Account ID') }]}
                                        >
                                            <Input placeholder="123456789012345" />
                                        </Form.Item>

                                        <Form.Item
                                            name="accessToken"
                                            label={t('Access Token')}
                                            rules={[{ required: true, message: t('Please enter Access Token') }]}
                                        >
                                            <Input.Password placeholder="IGQVJ..." />
                                        </Form.Item>

                                        <Form.Item
                                            name="autoReply"
                                            label={t('Enable Auto-Reply')}
                                            valuePropName="checked"
                                        >
                                            <Switch />
                                        </Form.Item>

                                        <Form.Item
                                            name="notifications"
                                            label={t('Enable Notifications')}
                                            valuePropName="checked"
                                            initialValue={true}
                                        >
                                            <Switch defaultChecked />
                                        </Form.Item>

                                        <Form.Item>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                icon={<SaveOutlined />}
                                                loading={saveIntegrationMutation.isLoading}
                                            >
                                                {t('Save Configuration')}
                                            </Button>
                                        </Form.Item>
                                    </Form>
                                </Panel>
                            </Collapse>
                        </Space>
                    </Card>
                </TabPane>
            </Tabs>
        </div>
    );
};

export default SocialIntegrations;
